import Settings from 'configs/Settings';
import { AuthService } from 'modules/auth';
import { UserService, UserStore } from 'modules/user';

import { SECTION } from './constants';
import { buildEventData } from './helper';

const DEFAULT_EVENT_NAME = 'AppClubAdminGenericEvent';

class GtagService {
  dataLayer = window.dataLayer || [];
  EVENTS = {
    INTERACTION: 'interaction',
    NONINTERACTION: 'noninteraction'
  };
  ACTIONS = {
    CLICK: 'Click',
    HOVER: 'Hover',
    SHOW: 'Show'
  };

  sendEvent = ({
    event,
    category = SECTION,
    action,
    where,
    label,
    subcategory
  }) => {
    window.dataLayer &&
      window.dataLayer.push(
        buildEventData({ category, event, action, where, label, subcategory })
      );
  };

  sendClick = (options) => {
    this.sendEvent({
      event: this.EVENTS.INTERACTION,
      action: this.ACTIONS.CLICK,
      ...options
    });
  };

  sendHover = (options) => {
    this.sendEvent({
      event: this.EVENTS.INTERACTION,
      actions: this.ACTIONS.HOVER,
      ...options
    });
  };

  sendShow = (options) => {
    this.sendEvent({
      event: this.EVENTS.NONINTERACTION,
      actions: this.ACTIONS.CLICK,
      ...options
    });
  };

  sendGtagEvent({ name, params }) {
    const gtag = window.gtag;

    if (Settings.isProduction() && typeof gtag === 'function') {
      const handledEventName = this.eventNameHandler(name);

      gtag('event', handledEventName, params);
    }
  }

  sendPageEvent = ({ subsection, subsection2, sourceType }) => {
    const [profile] = AuthService.roles || [];

    if (!subsection) {
      return;
    }

    this.dataLayer.push({
      event: 'pageview',
      userId: UserStore.marketplaceId,
      language: UserService.language.toLowerCase(),
      logged: AuthService.isLogged,
      section: SECTION,
      profile,
      subsection,
      ...(subsection2 && { subsection2 }),
      ...(sourceType && { sourceType })
    });
  };

  sendCustomEvent = ({ customEventName, customAttributes }) => {
    this.dataLayer.push({
      event: 'custom_event',
      custom_event_name: customEventName,
      ...customAttributes
    });
  };

  eventNameHandler(eventName) {
    if (eventName) {
      return `${eventName}`;
    }

    return DEFAULT_EVENT_NAME;
  }

  /**
   * @function
   * DEPRECATED - use sendEvent instead
   * Sends the given params as an evento to Google Analytics
   * @param {Object} params - Event properties
   * @param {string} params.category - Name that you supply as a way to group objects that you want to analyze
   * @param {string} params.action - Name of the type of event or interaction you want to measure for a particular object
   * @param {string} [params.label] - Provides additional information for events that you want to analyze,
   * @param {number} [params.value] - A numeric value associated with the event. the report adds the total values
   * based on each event count
   * Source: https://developers.google.com/analytics/devguides/collection/analyticsjs/events)
   */
  send = (params) => {
    const { name } = params;

    let event = {};

    const add = (param, prop) => {
      if (params[param] === null || params[param] === void 0) {
        return;
      }

      event = {
        ...event,
        [prop]: params[param]
      };
    };

    add('category', 'event_category');
    add('action', 'event_action');
    add('label', 'event_label');
    add('value', 'event_value');

    this.sendGtagEvent({
      name,
      params: event
    });
  };

  /**
   * @function
   * Create an event to be send to Google Analytics
   * @param {Object} config.common - Params that needs to be on each event
   * @param {...object} config - All events related to this context
   */
  createEvents(config) {
    const { common, ...events } = config;
    const formattedEvents = {};

    Object.keys(events).forEach((eventName) => {
      formattedEvents[eventName] = {
        ...common,
        ...events[eventName]
      };
    });

    return formattedEvents;
  }
}

export default new GtagService();
